import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import { apiErrorMessage } from '../../api/client';
import { AuthInput } from '../../components/auth/AuthInput';
import { PasswordInput } from '../../components/auth/PasswordInput';
import { GoogleButton, AuthDivider } from '../../components/auth/GoogleButton';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3; label: string } {
  if (pw.length < 8) return { score: 0, label: 'Too short' };
  let score = 1;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw)) score++;
  return {
    score: Math.min(score, 3) as 1 | 2 | 3,
    label: ['', 'Weak', 'Good', 'Strong'][Math.min(score, 3)]!,
  };
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirm?: string;
  }>({});
  const [submitting, setSubmitting] = useState(false);

  const strength = passwordStrength(password);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (displayName.trim().length === 0) next.name = 'Pick a display name';
    if (!EMAIL_RE.test(email)) next.email = 'Enter a valid email address';
    if (password.length < 8) next.password = 'At least 8 characters';
    if (confirm !== password) next.confirm = "Passwords don't match";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const res = await register(email, password, displayName.trim());
      toast.success('Check your inbox for a 6-digit verification code');
      navigate('/verify-email', { state: { email: res.email, sent: true } });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Registration failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section data-testid="page-register" className="mx-auto max-w-sm pt-6">
      <h1 className="text-2xl font-bold">Create account</h1>
      <div className="mt-6">
        <GoogleButton label="Sign up with Google" />
        <AuthDivider />
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <AuthInput
            label="Display name"
            id="display_name"
            autoComplete="nickname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={errors.name}
          />
          <AuthInput
            label="Email"
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <div>
            <PasswordInput
              label="Password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
            />
            {password.length > 0 ? (
              <div className="mt-2" data-testid="password-strength">
                <div className="flex gap-1" aria-hidden="true">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded ${
                        strength.score >= i
                          ? ['', 'bg-red-400', 'bg-amber-400', 'bg-emerald-500'][strength.score]
                          : 'bg-stone-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-1 text-xs text-stone-500">{strength.label}</p>
              </div>
            ) : null}
          </div>
          <PasswordInput
            label="Confirm password"
            id="confirm_password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={errors.confirm}
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="mt-4 text-sm text-stone-600">
          Already have an account?{' '}
          <Link to="/login" className="text-emerald-700 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </section>
  );
}
